import { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useBonusDetails } from "./useBonusQueries";
import { 
  BonusApplicationComment, 
  BonusApplicationHistoryItem, 
} from "../services/bonus/types";

export interface UseFormStateReturn {
  id: string | undefined;
  isReviewMode: boolean;
  isEditable: boolean;
  bonusSheetTitle: string;
  setBonusSheetTitle: React.Dispatch<React.SetStateAction<string>>;
  applicantInfo: { name: string; email: string };
  setApplicantInfo: React.Dispatch<React.SetStateAction<{ name: string; email: string }>>;
}

export const useFormState = (selectedRole: string) => {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const queryParams = new URLSearchParams(location.search);
  const isReviewMode = queryParams.get("mode") === "review";
  
  const { data: bonusDetails } = useBonusDetails(parseInt(id || "0"));
  
  const editableStatuses = [null, "Draft", "Rejected"];
  const isEditable = editableStatuses.includes(bonusDetails?.currentStatus ?? null);
  
  const [bonusSheetTitle, setBonusSheetTitle] = useState<string>(`${selectedRole} Quarterly Bonus`);
  const [applicantInfo, setApplicantInfo] = useState({
    name: localStorage.getItem("loggedInUserFullName") ?? "",
    email: localStorage.getItem("loggedInUserEmail") ?? "",
  });

  return {
    id,
    isReviewMode,
    isEditable,
    bonusSheetTitle,
    setBonusSheetTitle,
    applicantInfo,
    setApplicantInfo,
  };
};

export interface UseCommentsReturn {
  comment: string;
  setComment: React.Dispatch<React.SetStateAction<string>>;
  commentList: BonusApplicationComment[];
  setCommentList: React.Dispatch<React.SetStateAction<BonusApplicationComment[]>>;
  commentsAccordionExpanded: boolean;
  commentCounter: number;
  handleCommentChange: (value: string) => void;
  handleAddComment: (currentComment: string) => void;
  handleCommentsAccordionToggle: () => void;
}

export const useComments = (
  bonusDetails: any,
  selectedRole: string
): UseCommentsReturn => {
  const [comment, setComment] = useState<string>("");
  const [commentList, setCommentList] = useState<BonusApplicationComment[]>([]);
  const [commentsAccordionExpanded, setCommentsAccordionExpanded] = useState<boolean>(false);
  const [commentCounter, setCommentCounter] = useState<number>(0);

  useEffect(() => {
    if (bonusDetails?.comments) {
      setCommentList(bonusDetails.comments);
    }
  }, [bonusDetails]);

  const handleCommentChange = (value: string) => {
    setComment(value);
  };

  const handleAddComment = (currentComment: string) => {
    setComment(currentComment);

    if (comment.trim() !== "") {
      const centralDate = new Date(
        new Date().toLocaleString("en-US", { timeZone: "America/Chicago" })
      );

      const newComment: BonusApplicationComment = {
        commentId: commentCounter,
        bonusApplicationId: bonusDetails?.bonusApplicationId || 0,
        commentText: comment,
        commentedBy: localStorage.getItem("loggedInUserFullName") || "",
        commentedOn: centralDate.toISOString(),
        commentedByEmail: localStorage.getItem("loggedInUserEmail") || "",
        statusAtTimeOfComment: bonusDetails?.currentStatus.replaceAll("_", " ") ?? "Draft",
        statusIdAtTimeOfComment: 0,
        roleTypeName: selectedRole,
        roleTypeId: 0,
        isActive: true,
        isSystemGenerated: false,
      };

      setCommentList((prev) => [newComment, ...prev]);
      setComment("");
      setCommentCounter((prev) => prev - 1);
    }
  };

  const handleCommentsAccordionToggle = () => {
    setCommentsAccordionExpanded(!commentsAccordionExpanded);
  };

  return {
    comment,
    setComment,
    commentList,
    setCommentList,
    commentsAccordionExpanded,
    commentCounter,
    handleCommentChange,
    handleAddComment,
    handleCommentsAccordionToggle,
  };
};

export interface UseHistoryReturn {
  historyItems: BonusApplicationHistoryItem[];
  setHistoryItems: React.Dispatch<React.SetStateAction<BonusApplicationHistoryItem[]>>;
  historyAccordionExpanded: boolean;
  handleHistoryAccordionToggle: () => void;
}

export const useHistory = (bonusDetails: any): UseHistoryReturn => {
  const [historyItems, setHistoryItems] = useState<BonusApplicationHistoryItem[]>([]);
  const [historyAccordionExpanded, setHistoryAccordionExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (bonusDetails?.historyItems) {
      setHistoryItems(bonusDetails.historyItems);
    }
  }, [bonusDetails]);

  const handleHistoryAccordionToggle = () => {
    setHistoryAccordionExpanded(!historyAccordionExpanded);
  };

  return {
    historyItems,
    setHistoryItems,
    historyAccordionExpanded,
    handleHistoryAccordionToggle,
  };
};

export const getPreviousQuarterForForm = () => {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  
  let quarter = Math.floor(month / 3);
  let targetYear = year;
  
  if (quarter === 0) {
    quarter = 4;
    targetYear = year - 1;
  }
  
  return {
    quarter: quarter.toString(),
    year: targetYear.toString(),
  };
};

export const formatCurrency = (value: string | number): string => {
  if (!value || value === "" || isNaN(Number(value))) return "";
  return parseFloat(value.toString()).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

export const handleNumericInput = (
  e: React.ChangeEvent<HTMLInputElement>,
  options?: {
    type?: 'number' | 'decimal' | 'text',
    maxLength?: number,
  }
) => {
  let { value } = e.target;
  value = value.replace(/,/g, "").trim();

  const type = options?.type || "text";

  if (type === "number" && !(value === '' || /^[0-9]*$/.test(value))) {
    return null;
  }

  if (type === "decimal" && !(value === '' || /^\d*\.?\d{0,2}$/.test(value))) {
    return null;
  }

  if (options?.maxLength && value.length > options.maxLength) {
    return null;
  }

  return value;
};